import {
  Box,
  Button,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  ArrowsOutCardinal,
  Warning,
  XCircle
} from '@phosphor-icons/react';
import React, { memo, useEffect, useState } from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { useDebounceEffect } from '../../../util/useDebounceEffect';
import FormNode from '../Components/FormNode/FormNode';
import FormBuildInput from '../FormBuildInput/FormBuildInput';
import PopoverEdit from '../TitleNode/Components/PopoverEdit';

interface SelectedNode {
  id: string;
  flowId: string;
  name: string;
  type: string;
  outputType: string;
}

const OutputNodeDisplay = ({ id, type, data }: NodeProps) => {
  const user = useAuthStore(state => state.user);

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    url: '',
    selectedFlow: '',
    borderRadius: '4px',
    outputType: 'all',
    widthContainer: 800,
    heightContainer: 600,
    isGenerateLink: false,
    titleStyle: {
      fontSize: '20px',
      color: '#475467',
      textAlign: 'center',
      fontStyle: 'normal',
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 500,
      textDecoration: 'none',
      selStyle: [],
    },
    subtitleStyle: {
      fontSize: '14px',
      color: '#475467',
      textAlign: 'center',
      fontStyle: 'normal',
      fontFamily: 'Roboto, sans-serif',
      fontWeight: 400,
      textDecoration: 'none',
      selStyle: [],
    },
  });
  const reactFlow = useReactFlow();
  const [changes, setChanges] = useState<any>({
    directly: false,
    value: '',
    name: '',
  });
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingType, setEditingType] = useState('titleStyle');
  const [outputsNodes, setOutputsNodes] = useState<any[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode>({
    id: '',
    flowId: '',
    name: '',
    type: '',
    outputType: 'standard',
  });

  useEffect(() => {
    if (data && !changes?.directly) {
      const selectedFlow = data?.selectedFlow || 'none';

      setFormData(prevFormData => ({
        ...prevFormData,
        title: data.title || '',
        subtitle: data.subtitle || '',
        borderRadius: data.borderRadius || '4px',
        url: data.url || '',
        selectedFlow: selectedFlow,
        outputType: data.outputType || 'standard',
        titleStyle: {
          ...prevFormData?.titleStyle,
          ...data.titleStyle,
        },
        subtitleStyle: {
          ...prevFormData?.subtitleStyle,
          ...data.subtitleStyle,
        },
      }));

      const varOutputNodes =
        data.varOutputNodes?.map((node: any) => {
          return {
            id: node.id,
            label: node.label,
            flowName: node.flowName,
            type: 'varOutput',
          };
        }) || [];

      setOutputsNodes([...varOutputNodes]);

      // Set selected node when data changes
      if (selectedFlow !== 'none') {
        const selectedNode = [...varOutputNodes].find(
          node => node.id === selectedFlow,
        );
        if (selectedNode) {
          setSelectedNode({
            id: selectedNode.id,
            flowId: selectedNode.flowId,
            name: selectedNode.label,
            type: selectedNode.type,
            outputType: data.outputType || 'standard',
          });
        }
      }
    }
  }, [data, changes]);

  const hasOutputNode = data?.varOutputNodes?.length > 0;

  const onChanges = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;

    const data = {
      [name]: value,
    };

    setFormData({ ...formData, ...data });
    setChanges({ directly: true, data });
  };

  const handleClick = (type: string) => {
    setOpenEditModal(true);
    setEditingType(type);
  };

  const handleClose = () => {
    setOpenEditModal(false);
  };

  useDebounceEffect(
    () => {
      if (changes.directly) {
        const nodes = reactFlow.getNodes();

        const mappedNodes = nodes.map(node => {
          if (node.id === id) {
            if (changes?.data?.widthData) {
              return {
                ...node,
                width: changes.data.widthData?.width,
                style: {
                  width: changes.data.widthData?.width,
                },
                data: {
                  ...node.data,
                  widthData: changes.data.widthData,
                },
              };
            }
            
            if (changes?.name === 'widthData') {
              return {
                ...node,
                width: changes.value?.width,
                style: {
                  width: changes.value?.width,
                },
                data: {
                  ...node.data,
                  [changes.name]: changes.value,
                },
              };
            }

            return {
              ...node,
              data: {
                ...node.data,
                ...changes.data,
              },
            };
          }
          return node;
        });

        reactFlow.setNodes(mappedNodes);

        setChanges({ directly: false });
      }
    },
    [changes],
    500,
  );

  const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (node: any) => {
    setSelectedNode({
      id: node.id,
      flowId: node.flowId,
      name: node.label,
      type: node.type,
      // widthContainer: node.widthContainer || 800,
      // heightContainer: node.heightContainer || 600,
      outputType: node.outputType || 'all',
    });
  };

  const handleSubmitSelectedNode = () => {
    const data = {
      selectedFlow: selectedNode.id,
      outputType: selectedNode.outputType,
    };
    setFormData({ ...formData, ...data });
    setChanges({ directly: true, data });
    setAnchorEl(null);
  };

  const handleModeChange = (event: any) => {
    const value = event.target.value;

    setSelectedNode(prev => ({
      ...prev,
      outputType: value === 'standard' ? 'standard' : 'editor',
    }));
  };

  return (
    <FormNode id={id} type={type} borderRadius={formData.borderRadius}>
      <FormBuildInput
        data={data}
        formData={{
          name: 'title',
          value: formData.title,
          placeholder: 'Add title here...',
          style: formData.titleStyle,
          titleEmpty: 'Title',
        }}
        editingType={editingType}
        editType={'titleStyle'}
        handleClick={handleClick}
        isEditing={isEditing}
        onChanges={onChanges}
        setEditingType={setEditingType}
        setIsEditing={setIsEditing}
      />

      <FormBuildInput
        data={data}
        formData={{
          name: 'subtitle',
          value: formData.subtitle,
          placeholder: 'Add subtitle here...',
          style: formData.subtitleStyle,
          titleEmpty: 'Subtitle (Optional)',
        }}
        editingType={editingType}
        editType={'subtitleStyle'}
        handleClick={handleClick}
        isEditing={isEditing}
        onChanges={onChanges}
        setEditingType={setEditingType}
        setIsEditing={setIsEditing}
      />

      {openEditModal && (
        <PopoverEdit
          open={openEditModal}
          handleClose={handleClose}
          onChange={onChanges}
          selectedStyle={formData?.[editingType as keyof typeof formData]}
          editingType={editingType}
          modalStyle={{
            bgColor: data?.bgColor ? data?.bgColor : '#ffffff',
            widthData: data?.widthData,
            borderRadius: formData.borderRadius || '4px',
          }}
        />
      )}

      {/* <Box
        sx={{
          border: `1px dashed var(--Neutral-300, #D0D5DD)`,
          borderRadius: '8px',
          padding: '30px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: '16px',
          mb: '24px',
        }}
      >
        <Box textAlign={'center'}>
          <TextAlignLeft size={24} color="#667085" />
          <Typography fontSize={'12px'} color={'#475467'}>
            Your output will be displayed in text format here once you submit
            your form. To test this, enter preview mode or publish your form.
          </Typography>
        </Box>
      </Box> */}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {hasOutputNode ? (
          <Button
            variant="outlined"
            color="primary"
            onClick={handleClickPopover}
            sx={{
              width: '200px',
              fontSize: '12px',
            }}
          >
            {formData.selectedFlow !== 'none'
              ? (
                  outputsNodes.find(
                    node => node.id === formData.selectedFlow,
                  ) as { label: string } | undefined
                )?.label || 'Choose Output Node'
              : 'Choose Output Node'}
          </Button>
        ) : (
          <Box display={'flex'} alignItems={'center'} gap="8px">
            <Warning size={16} color="#F57F17" weight="fill" />
            <Typography
              variant="body2"
              sx={{
                color: '#475467',
                fontSize: '12px',
                fontWeight: 400,
              }}
            >
              Please connect an{' '}
              <Typography component={'span'} fontWeight={600} fontSize={'12px'}>
                Output Node
              </Typography>{' '}
              to your workflow first.
            </Typography>
          </Box>
        )}
      </Box>

      <Popover
        id={id}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          width={'629px'}
          height={'100%'}
          p={'16px'}
          borderRadius="16px"
          gap={'24px'}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
            <Box
              display={'flex'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Box display={'flex'} alignItems={'center'} gap={'8px'}>
                <ArrowsOutCardinal size={16} color="#101828" />
                <Typography
                  fontSize={'14ox'}
                  fontWeight={600}
                  color={'#000000'}
                >
                  Choose Output
                </Typography>
              </Box>
              <XCircle
                size={24}
                color="#667085"
                cursor={'pointer'}
                onClick={() => setAnchorEl(null)}
              />
            </Box>

            <Typography fontSize={'14px'} fontWeight={400} color={'#475467'}>
              Select the Output node that meets your form's data needs. It will
              provide the required output when your form is published.
            </Typography>
          </Box>

          <Box
            display={'flex'}
            flexDirection={'column'}
            gap={'8px'}
            sx={{
              overflowY: 'auto',
              maxHeight: '250px',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#F2F4F7',
                padding: '2px',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#98A2B3',
                borderRadius: '4px',
              },
            }}
          >
            {outputsNodes?.map((node: any, index: number) => {
              const isSelected = selectedNode?.id === node.id;

              return (
                <Box
                  key={node?.id}
                  sx={{
                    display: 'flex',

                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: isSelected
                      ? '1px solid #D0D5DD'
                      : '1px solid transparent',
                    backgroundColor: isSelected ? '#F9FAFB' : 'transparent',
                    borderRadius: '8px',
                  }}
                >
                  <Box
                    onClick={() => handleSelect(node)}
                    display={'flex'}
                    alignItems={'center'}
                    gap={'8px'}
                    p={'8px'}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: isSelected ? '#101828' : '#475467',
                        fontWeight: 400,
                      }}
                    >
                      {node?.label}
                    </Typography>
                  </Box>

                  {isSelected && (
                    <RadioGroup
                      value={selectedNode.outputType}
                      onChange={handleModeChange}
                      row
                      sx={{ display: 'flex', gap: '8px', pl: '8px' }}
                    >
                      <FormControlLabel
                        value="standard"
                        control={<Radio size="small" />}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                          },
                          color: '#101828',
                        }}
                        label="Standard"
                      />

                      <FormControlLabel
                        value="editor"
                        control={<Radio size="small" />}
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            fontSize: '14px',
                          },
                          color: '#101828',
                        }}
                        label="Editor"
                      />
                    </RadioGroup>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box display={'flex'} justifyContent={'flex-end'}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitSelectedNode}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Popover>
    </FormNode>
  );
};

export default memo(OutputNodeDisplay);
